import { RUMAAL_RANKS } from './constants';

// Numeric value of each belt: White=0 (beginner) ... Black=5 (most advanced).
// Used to average a class's overall Rumaal level from its students.
const RANK_VALUES = RUMAAL_RANKS.reduce((acc, rank, index) => {
  acc[rank] = index;
  return acc;
}, {});

/**
 * Calculate a class's overall Rumaal rank from its students.
 *
 * Instead of "the most common belt" (which let a few unranked students drag a
 * whole class down to White), this uses a weighted average where higher belts
 * carry more weight, so the class rank reflects the group's real skill level
 * and rises as students advance.
 *
 * Rules:
 *  - Students with no belt assigned yet are ignored (they don't count as White).
 *  - Each student is weighted by (belt value + 1), so a Green student influences
 *    the result more than a White one.
 *  - The weighted average is rounded to the nearest belt (ties round up).
 *
 * @param {Array<{ rumaalRank?: string }>} students
 * @returns {string} one of RUMAAL_RANKS
 */
export const calculateClassRumaalRank = (students) => {
  if (!Array.isArray(students) || students.length === 0) {
    return 'White';
  }

  let weightedSum = 0;
  let totalWeight = 0;

  students.forEach((student) => {
    const rank = student?.rumaalRank;
    // Ignore unranked students entirely (null / undefined / '' / unknown belt).
    if (rank == null || rank === '' || !(rank in RANK_VALUES)) {
      return;
    }
    const value = RANK_VALUES[rank];
    const weight = value + 1; // higher belts carry more weight
    weightedSum += value * weight;
    totalWeight += weight;
  });

  // No students have a belt assigned yet → default to White.
  if (totalWeight === 0) {
    return 'White';
  }

  const average = weightedSum / totalWeight;
  const index = Math.round(average); // ties round up → class trends upward
  return RUMAAL_RANKS[index] || 'White';
};
